const { currencyLocale } = require('../constants/currencies');
const { round } = require('../constants/math');
const { dayjs } = require('./date');

function numberFormat(value, opts, locale = 'en-GB') {
  return value != null ? new Intl.NumberFormat(locale, opts).format(value) : '';
}

function percentFormat(value, opts, locale = 'en-GB') {
  return numberFormat(value, { style: 'percent', ...opts }, locale);
}

function currencyFormat(value, opts, locale) {
  return value != null ? numberFormat(value, { style: 'currency', maximumFractionDigits: 2, minimumFractionDigits: 0, ...opts, currency: opts?.currency || 'EUR' }, locale || currencyLocale[opts?.currency || 'EUR']) : '';
}

function boolFormat(value) {
  if (value === true || value === 'true' || value === 1 || value === '1') {
    return 'Yes';
  }
  if (value === false || value === 'false' || value === 0 || value === '0') {
    return 'No';
  }
  return '';
}

function dateFormat(value, opts, locale = 'en-GB') {
  return value != null ? new Intl.DateTimeFormat(locale, { year: 'numeric', day: 'numeric', month: 'short', ...opts }).format(dayjs.utc(value).toDate()) : '';
}

function timeFormat(value, opts, locale = 'en-GB') {
  return value != null ? new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: 'numeric', ...opts }).format(dayjs.utc(value).toDate()) : '';
}

function dateTimeFormat(value, opts, locale = 'en-GB') {
  return value != null ? new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: 'numeric',
    ...opts,
  }).format(dayjs.utc(value).toDate()) : '';
}

function relativeDateFormat(value, opts, locale = 'en-GB') {
  return value != null ? new Intl.RelativeTimeFormat(locale, opts).format(dayjs.utc(value).toDate()) : '';
}

function durationFormat(milliseconds) {
  if (milliseconds == null) {
    return '';
  }

  if (milliseconds === 0) {
    return '0';
  }

  if (milliseconds < 10) {
    return `${round(milliseconds, 2)}ms`;
  }

  if (milliseconds < 1000) {
    return `${round(milliseconds)}ms`;
  }

  const seconds = Math.round(milliseconds / 1000);

  if (seconds < 10) {
    return `${round(milliseconds / 1000, 2)}s`;
  }

  const remainingSeconds = seconds % 60;
  const minutes = Math.floor(seconds / 60);
  const remainingMinutes = minutes % 60;
  const hours = Math.floor(minutes / 60);

  const values = [];

  if (hours > 1) {
    values.push(`${hours}h`);
  }
  if (remainingMinutes > 0) {
    values.push(`${remainingMinutes}m`);
  }
  if (remainingSeconds > 0) {
    values.push(`${remainingSeconds}s`);
  }

  return values.join(' ');
}

function durationDecimalFormat(milliseconds) {
  if (milliseconds == null) {
    return '';
  }

  if (milliseconds < 1000) {
    return `${round(milliseconds, 2)} ms`;
  }

  const seconds = milliseconds / 1000;

  if (seconds < 60) {
    return `${round(seconds, 2)} sec`;
  }

  const minutes = seconds / 60;

  if (minutes < 60) {
    return `${round(minutes, 2)} min`;
  }

  const hours = minutes / 60;

  if (hours < 24) {
    return `${round(hours, 2)} hours`;
  }

  const days = hours / 24;

  if (days < 365) {
    return `${round(days, 2)} days`;
  }

  const years = days / 365;

  return `${round(years, 2)} years`;
}

function durationClockFormat(milliseconds) {
  if (milliseconds == null) {
    return '';
  }

  const duration = milliseconds;

  if (duration < 2 * 1000) {
    return `${round(duration / 1000, 3)}s`;
  }

  const seconds = Math.floor(duration / 1000);

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
  const formattedSeconds = remainingSeconds < 10 ? `0${remainingSeconds}` : remainingSeconds;

  if (hours > 0) {
    const formattedHours = hours < 10 ? `0${hours}` : hours;
    return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
  }

  return `${formattedMinutes}:${formattedSeconds}`;
}

function periodFormat(period, opts, locale = 'en-GB') {
  if (!period?.startDate || !period?.endDate) return '';

  const start = dateFormat(period.startDate, opts, locale);
  const end = dateFormat(period.endDate, opts, locale);

  return `${start}${start !== end ? ` - ${end}` : ''}`;
}

function urlFormat(url) {
  return url?.replace(/https?:\/\//, '');
}

function getFormatter(type, { maximumFractionDigits, currency } = {}) {
  switch (type) {
    case 'number':
      return v => numberFormat(v, { maximumFractionDigits: maximumFractionDigits ?? 1 });
    case 'percent':
      return v => percentFormat(v, { maximumFractionDigits: maximumFractionDigits ?? 0 });
    case 'bool':
    case 'boolean':
      return boolFormat;
    case 'date':
      return dateFormat;
    case 'time':
      return timeFormat;
    case 'datetime':
      return dateTimeFormat;
    case 'duration':
    case 'milliseconds':
      return durationFormat;
    case 'seconds':
      return seconds => durationFormat(seconds * 1000);
    case 'relativeDate':
      return relativeDateFormat;
    case 'url':
      return urlFormat;
    case 'currency':
      return v => {
        if (!currency) {
          return '';
        }
        return currencyFormat(v, {
          currency,
          maximumFractionDigits: maximumFractionDigits ?? 2,
        });
      };
    default:
      return v => v?.toString();
  }
}

module.exports = {
  numberFormat,
  percentFormat,
  currencyFormat,
  dateFormat,
  timeFormat,
  durationFormat,
  durationDecimalFormat,
  durationClockFormat,
  periodFormat,
  getFormatter,
};
